
import { supabase } from '../../supabase';
import { Paper, PaperAuthor } from '@/types/paper';
import { mapToPaper, deduplicateAuthors } from '../paper-mappers';
import { toast } from 'sonner';
import { fetchUsersWithCache } from './userCache';

const SLIM_PAPER_SELECT = `
  id, title, abstract, author_id, category, keywords,
  current_version, published_at, updated_at, views,
  external_url, created_at, authors, license,
  license_compliance_status, ai_review_count,
  average_rating, review_count, comment_count, visibility
`;

/**
 * Get papers by author ID — optimised version.
 * Uses slim selects, skips version fetching, and leverages shared user cache.
 */
export async function getPapersByAuthorId(authorId: string, signal?: AbortSignal): Promise<Paper[]> {
  try {
    if (!authorId || typeof authorId !== 'string') {
      console.error('Invalid author ID:', authorId);
      return [];
    }

    // 1. Get paper IDs via RPC
    const { data: paperIds, error: paperIdsError } = await supabase
      .rpc('get_papers_by_author', { author_uuid: authorId });

    if (paperIdsError) {
      console.error('Error fetching paper IDs for author:', paperIdsError);
      toast.error('Failed to load author papers', { description: paperIdsError.message });
      return [];
    }

    if (!paperIds || paperIds.length === 0) {
      return [];
    }

    const ids = paperIds.map(item => item.paper_id);

    // 2. Fetch papers with slim select
    const { data: papers, error: papersError } = await supabase
      .from('papers')
      .select(SLIM_PAPER_SELECT)
      .in('id', ids)
      .order('updated_at', { ascending: false });

    if (papersError) {
      console.error('Error fetching papers for author:', papersError);
      toast.error('Failed to load author papers', { description: papersError.message });
      return [];
    }

    if (!papers || papers.length === 0) {
      return [];
    }

    // 3. Collect author IDs for user lookup
    const candidateAuthorIds = new Set<string>();
    papers.forEach(paper => {
      if (paper.author_id) candidateAuthorIds.add(paper.author_id);
      if (Array.isArray(paper.authors)) {
        paper.authors.forEach((author: any) => {
          if (author.isPlatformUser && author.id) candidateAuthorIds.add(author.id);
        });
      }
    });

    // 4. Fetch users via shared cache (slim select, cached)
    const usersMap = await fetchUsersWithCache(Array.from(candidateAuthorIds), signal);

    // 5. Map papers — pass empty versions array (cards don't need version data)
    const mappedPapers = papers.map(paper => {
      const mappedPaper = mapToPaper(paper, []);

      mappedPaper.reviewCount = paper.review_count ?? 0;
      mappedPaper.commentCount = paper.comment_count ?? 0;
      mappedPaper.averageRating = paper.average_rating != null ? Number(paper.average_rating) : undefined;

      if (mappedPaper.authors && mappedPaper.authors.length > 0) {
        mappedPaper.authors = mappedPaper.authors.map((author) => {
          if (author.isPlatformUser && usersMap.has(author.id)) {
            const user = usersMap.get(author.id);
            return {
              ...author,
              id: user.id,
              name: user.name,
              institution: user.institution || undefined,
              role: user.role || undefined,
              isPlatformUser: true
            } as PaperAuthor;
          }
          return author;
        });
        mappedPaper.authors = deduplicateAuthors(mappedPaper.authors);
      }

      return mappedPaper;
    });

    return mappedPapers;
  } catch (error) {
    console.error('Unexpected error in getPapersByAuthorId:', error);
    toast.error('An unexpected error occurred', {
      description: 'Could not fetch author papers due to a system error.'
    });
    return [];
  }
}
